import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { CUSTOM_MASK } from '@helpers/custom-validator';
import { enumIdentifierQualifier } from '@data-layer/enums/common';
import { OrganizationModel } from '@app/data-layer/models/organizations/organization-model';

@Component({
  selector: 'company-employee-status',
  templateUrl: './company-employee-status.component.html',
  styleUrls: ['./company-employee-status.component.scss'],
})
export class CompanyEmployeeStatusComponent implements OnInit, OnDestroy {
  @Input() public organization: OrganizationModel;

  public employeeControl: FormControl<boolean> = new FormControl<boolean>(true);
  public orgFormGroup: UntypedFormGroup;
  public CUSTOM_MASK: any = CUSTOM_MASK;

  private subscription: Subscription[] = [];

  public get isEmployee(): boolean {
    return this.employeeControl.value;
  }

  public ngOnInit(): void {
    this.orgFormGroup = new UntypedFormGroup({
      businessName: new UntypedFormControl(this.organization?.businessName || null),
      identifier: new UntypedFormControl(this.organization?.identifier || null),
      identifierQualifier: new UntypedFormControl(this.organization?.identifierQualifier || enumIdentifierQualifier.EIN),
      dateOfBirth: new UntypedFormControl(this.organization?.dateOfBirth || null),
    });

    this.subscription.push(
      this.employeeControl.valueChanges.subscribe((isEmployee: boolean) => {
        this.toggleOrgValidators(!isEmployee);
      }),
      this.orgFormGroup.get('identifierQualifier').valueChanges.subscribe((qual: enumIdentifierQualifier) => {
        // Run after identifier-qualifier-update child component has set its own validators
        Promise.resolve().then(() => this.syncQualifierValidators(qual));
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.forEach((s) => s.unsubscribe());
  }

  public getOrgFormData(): Partial<OrganizationModel> {
    return this.orgFormGroup.getRawValue();
  }

  public isOrgFormValid(): boolean {
    if (this.isEmployee) {
      return true;
    }
    this.orgFormGroup.markAllAsTouched();
    return this.orgFormGroup.valid;
  }

  private static readonly IDENTIFIER_KEY_BY_QUALIFIER: { [key: string]: string } = {
    [enumIdentifierQualifier.EIN]: 'org_identifier_ein',
    [enumIdentifierQualifier.SSN]: 'org_identifier_ssn',
    [enumIdentifierQualifier.ANI]: 'org_identifier_ani',
  };

  public getInvalidOrgFields(): { [key: string]: string } {
    if (this.isEmployee) {
      return {};
    }
    // Ensure validators are current before checking
    const qual = this.orgFormGroup.get('identifierQualifier').value;
    this.syncQualifierValidators(qual);

    const fields: { [key: string]: string } = {};
    Object.keys(this.orgFormGroup.controls).forEach((field) => {
      const control = this.orgFormGroup.get(field);
      control.markAsTouched({ onlySelf: true });
      if (control.invalid) {
        let prefixedKey: string;
        if (field === 'businessName') {
          prefixedKey = 'org_business_name';
        } else if (field === 'identifier') {
          prefixedKey = CompanyEmployeeStatusComponent.IDENTIFIER_KEY_BY_QUALIFIER[qual] || 'org_identifier_ein';
        } else if (field === 'dateOfBirth') {
          prefixedKey = 'org_date_of_birth';
        } else {
          prefixedKey = field;
        }
        fields[prefixedKey] = prefixedKey;
      }
    });
    return fields;
  }

  private toggleOrgValidators(required: boolean): void {
    const businessNameControl = this.orgFormGroup.get('businessName');
    if (required) {
      businessNameControl.setValidators([Validators.required]);
      this.syncQualifierValidators(this.orgFormGroup.get('identifierQualifier').value);
    } else {
      businessNameControl.clearValidators();
      this.orgFormGroup.get('identifier').clearValidators();
      this.orgFormGroup.get('dateOfBirth').clearValidators();
    }
    businessNameControl.updateValueAndValidity();
    this.orgFormGroup.get('identifier').updateValueAndValidity();
    this.orgFormGroup.get('dateOfBirth').updateValueAndValidity();
  }

  private syncQualifierValidators(qual: enumIdentifierQualifier): void {
    if (this.isEmployee) {
      return;
    }
    const identifierControl = this.orgFormGroup.get('identifier');
    const dateOfBirthControl = this.orgFormGroup.get('dateOfBirth');

    // The child component sets format validators (EIN/SSN) on identifier,
    // but clears to null for ANI. We ensure required is always present.
    const currentValidators = identifierControl.validator;
    if (!currentValidators || !currentValidators({ value: '' } as any)?.required) {
      // If child cleared validators (ANI case) or no required, add it back
      identifierControl.setValidators([Validators.required]);
      identifierControl.updateValueAndValidity({ emitEvent: false });
    }

    // dateOfBirth: required only for SSN
    if (qual === enumIdentifierQualifier.SSN) {
      dateOfBirthControl.setValidators([Validators.required]);
    } else {
      dateOfBirthControl.clearValidators();
    }
    dateOfBirthControl.updateValueAndValidity({ emitEvent: false });
  }
}
